<div class="proposal-dialog d-flex flex-column">
    <!-- START: Header -->
    <div class="d-flex align-items-center gap-4 px-8 py-5 border-bottom">
        <i class="fas fa-file-signature fs-1 text-primary"></i>
        <div class="flex-grow-1">
            <div class="fs-3 fw-bold text-dark lh-sm"> {{ 'PROPOSAL_DIALOG_TITLE' | translate }} </div>
            <div class="fs-7 text-muted"> {{ 'PROPOSAL_DIALOG_SUBTITLE' | translate }} </div>
        </div>
        <button class="btn btn-icon btn-sm btn-light" [disabled]="submitting" (click)="cancel()" [matTooltip]="'CANCEL' | translate">
            <i class="fas fa-times"></i>
        </button>
    </div>
    <!-- END: Header -->

    <div class="proposal-content flex-grow-1 overflow-auto px-8 py-5">
        <!-- START: Summary -->
        <div class="d-flex align-items-center gap-3 mb-3">
            <span class="step-number">1</span>
            <span class="fs-5 fw-bold text-dark"> {{ 'PROPOSAL_SUMMARY_TITLE' | translate }} </span>
        </div>

        <div class="d-flex flex-wrap gap-2 mb-3">
            <span class="summary-chip">
                <i class="fas fa-plug"></i> {{ 'PROPOSAL_EQUIPMENT_COUNT' | translate: { count: equipments.length } }}
            </span>
            <span class="summary-chip" *ngIf="selectedPrices.length > 0">
                <i class="fas fa-calendar"></i> {{ supplyPeriod | dateRange:'MMM yy':undefined:userLocale }}
            </span>
        </div>

        <div class="rounded border border-1 border-solid border-dark-clarity overflow-hidden">
            <table class="table align-middle gs-4 gy-2 mb-0">
                <thead>
                    <tr class="fs-8 fw-bold text-muted text-uppercase bg-light">
                        <th> {{ 'PERIOD' | translate }} </th>
                        <th class="text-end"> {{ 'PROTECTED_SHARE' | translate }} </th>
                        <th class="text-end"> {{ 'COVER_PRICE' | translate }} </th>
                    </tr>
                </thead>
                <tbody class="fs-7">
                    <tr *ngFor="let price of summary.prices" class="border-top border-gray-200">
                        <td>
                            <i class="fas fa-lock text-primary fs-8 me-2"></i>
                            <span class="fw-bold text-dark"> {{ price.label }} </span>
                            <span class="text-muted fs-8 ms-1"> {{ price.description }} </span>
                        </td>
                        <td class="text-end fw-bold text-dark"> {{ price.coverPercentage }}% </td>
                        <td class="text-end text-nowrap"> {{ price.price | number:'1.2-2' }} €/MWh </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="d-flex flex-wrap gap-2 mt-3" *ngIf="summary.years.length > 0">
            <div *ngFor="let year of summary.years" class="year-estimate rounded bg-light-primary px-3 py-2 fs-8">
                <span class="fw-bold text-dark fs-7 me-1"> {{ year.year }} </span>
                <span class="text-muted"> {{ 'YEAR_PROTECTED_SHARE' | translate: { percentage: (year.coverPercentage | number:'1.0-0') } }} · </span>
                <span class="fw-bold text-primary"> ~{{ year.totalCostEstimate | number:'1.0-0' }} € </span>
                <span class="text-muted"> ({{ year.averagePriceEstimate | number:'1.2-2' }} €/MWh) </span>
            </div>
        </div>

        <div class="fs-8 text-muted mt-3">
            <i class="fas fa-circle-info me-1"></i> {{ 'PROPOSAL_INDEXED_NOTE' | translate }}
        </div>
        <!-- END: Summary -->

        <!-- START: Billing (a contract keeps its own billing terms, so it's only asked for opportunities) -->
        <ng-container *ngIf="!isContract">
        <div class="d-flex align-items-center gap-3 mt-7 mb-3">
            <span class="step-number">2</span>
            <span class="fs-5 fw-bold text-dark"> {{ 'PROPOSAL_BILLING_TITLE' | translate }} </span>
        </div>

        <div *ngIf="!conditionsLoaded" class="d-flex align-items-center gap-3 text-muted fs-7 py-4">
            <span class="spinner-border spinner-border-sm text-primary"></span> {{ 'LOADING_PRODUCT_CONDITIONS' | translate }}
        </div>

        <div *ngIf="conditionsLoaded && allowedPaymentTypes.length == 0" class="rounded bg-light-warning text-warning fs-7 p-3">
            <i class="fas fa-triangle-exclamation text-warning me-1"></i> {{ 'NO_PAYMENT_TYPES_AVAILABLE' | translate }}
        </div>

        <form autocomplete="off" [formGroup]="form" *ngIf="allowedPaymentTypes.length > 0" class="row g-5">
            <!-- Payment Type -->
            <div class="col-12 col-md-6">
                <label class="form-label required" for="paymentType">{{ 'PAYMENT_TYPE' | translate }}</label>
                <is-select [menuFitInput]="true" [Options]="allowedPaymentTypes" formControlName="paymentType" [smallInput]="true" [solid]="false">
                </is-select>
                <div class="fs-8 text-muted mt-1"> {{ 'PAYMENT_TYPE_HINT' | translate }} </div>
            </div>

            <!-- Electronic Invoice -->
            <div class="col-12 col-md-6">
                <label class="form-label" for="invoiceEmail">{{ 'ELECTRONIC_INVOICE' | translate }}</label>
                <div class="option-box rounded border border-1 border-solid border-dark-clarity px-3 py-2">
                    <mat-checkbox id="electronicInvoice" formControlName="electronicInvoice">
                        <span class="fs-7"> {{ 'RECEIVE_INVOICE_BY_EMAIL' | translate }} </span>
                    </mat-checkbox>
                    <input *ngIf="form.controls.electronicInvoice.value" class="form-control form-control-sm mt-2" id="invoiceEmail" formControlName="invoiceEmail" placeholder="{{'EMAIL' | translate}}"
                        [class.is-invalid]="form.controls.invoiceEmail.invalid && form.controls.invoiceEmail.touched">
                </div>
                <div class="fs-8 text-muted mt-1" *ngIf="electronicInvoiceRequired"> {{ 'ELECTRONIC_INVOICE_REQUIRED_HINT' | translate }} </div>
            </div>

            <!-- Green Energy -->
            <div class="col-12">
                <div class="option-box rounded border border-1 border-solid border-dark-clarity px-3 py-2 d-flex align-items-center gap-3" [class.opacity-50]="!greenEnergyAvailable">
                    <i class="fas fa-leaf text-success fs-4"></i>
                    <div class="flex-grow-1">
                        <mat-checkbox id="isGreenEnergy" formControlName="isGreenEnergy">
                            <span class="fs-7 fw-bold text-dark"> {{ 'GREEN_ENERGY' | translate }} </span>
                        </mat-checkbox>
                        <div class="fs-8 text-muted ms-10">
                            {{ (greenEnergyAvailable ? 'GREEN_ENERGY_HINT' : 'GREEN_ENERGY_UNAVAILABLE_HINT') | translate }}
                        </div>
                    </div>
                </div>
            </div>
        </form>
        </ng-container>
        <!-- END: Billing -->
    </div>

    <!-- START: Footer -->
    <div class="d-flex flex-wrap justify-content-end align-items-center gap-3 px-8 py-4 border-top bg-white">
        <span class="text-muted fs-7 me-auto" *ngIf="!isContract && conditionsLoaded && allowedPaymentTypes.length > 0 && form.controls.paymentType.invalid">
            {{ 'PROPOSAL_SELECT_PAYMENT_HINT' | translate }}
        </span>
        <button class="btn btn-light" [disabled]="submitting" (click)="cancel()">
            {{ 'CANCEL' | translate }}
        </button>
        <button class="btn btn-primary" [disabled]="submitBlocked" (click)="submitProposal()">
            <span *ngIf="submitting" class="spinner-border spinner-border-sm me-2"></span>
            <i *ngIf="!submitting" class="fas fa-file-signature fs-7"></i>
            {{ (submitting ? 'CREATING_PROPOSAL' : 'SAVE_AND_CREATE_PROPOSAL') | translate }}
        </button>
    </div>
    <!-- END: Footer -->
</div>
